import {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {getLayoutCounts} from 'src/openapi/admin/apis/UserLayoutsApi';
import {useAppDispatch, useAppSelector} from 'src/store/hooks';

import {makeSelectLayoutsCountByUserId, selectLayoutsCountReceivedAt} from '../slices/layoutsCountSelectors';
import {LayoutsCountPageState, invalidateLayoutsCount} from '../slices/layoutsCountSlice';

type Props = {
    userId: number,
    autoload?: boolean,
    ttlMs?: number,
};

export const useUserLayoutsCount = ({userId, autoload = true, ttlMs = 120_000}: Props) => {
    const dispatch = useAppDispatch();
    const sliceDispatch = useDispatch();

    const selectLayoutsCount = useMemo(
        () => makeSelectLayoutsCountByUserId(userId),
        [userId],
    );

    const {total, status, error} = useAppSelector(selectLayoutsCount);

    const refetch = useCallback(() => {
        dispatch(getLayoutCounts({userId}));
    }, [dispatch, userId]);

    const invalidate = useCallback(() => {
        sliceDispatch(invalidateLayoutsCount(userId));
    }, [sliceDispatch, userId]);

    const receivedAt = useAppSelector(
        (state: LayoutsCountPageState) => selectLayoutsCountReceivedAt(state, userId),
    );

    useEffect(() => {
        if (!autoload) {
            return;
        }

        if (status === 'idle' || status === 'failed') {
            refetch();
            return;
        }

        if (status === 'succeeded' && receivedAt) {
            const now = Date.now();
            if (now - receivedAt > ttlMs) {
                refetch();
            }
        }
    }, [autoload, status, receivedAt, ttlMs, refetch]);

    return {
        total,
        status,
        error,
        refetch,
        invalidate,
        isFetching: status === 'loading',
    };
};
